import React, { useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { isMobile, isTablet } from "react-device-detect";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  onSnapshot,
  getDoc,
  setDoc,
  increment,
} from "firebase/firestore";
import { Database, getDatabase } from "firebase/database";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import DebugFooter from "components/DebugFooter";
import Navbar from "components/Navbar";
import Friends from "components/friends/Friends";
import PausePopup from "components/popups/PausePopup";
import UserProfile, { MAX_USERNAME_LENGTH } from "views/UserProfile";
import Home from "views/Home";
import GameView from "views/Game/GameView";
import { RootState } from "stores/Store";
import { setUser } from "stores/user/UserSlice";
import { setGame } from "stores/game/GameSlice";
import { setLobby } from "stores/lobby/LobbySlice";
import { Game, GameStateEnum } from "interfaces/game";
import { Lobby } from "interfaces/lobby";
import { User } from "interfaces/user";
import "styles/Global.scss";
import DebugPage from "components/DebugPage";
import CreateOrJoinGame from "views/CreateOrJoinGame";
import About from "views/About";
import Lab from "views/Lab";
import PrivacyPolicy from "views/PrivacyPolicy";
import TermsOfService from "views/TermsOfService";
import Contact from "views/Contact";
import Footer from "components/Footer";
import FAQ from "components/FAQ";
import { defaultStartingElo, fetchLatestPatch } from "constants/gameDefaults";

import app from "./firebaseConfig";

import Leaderboard from "./views/Leaderboard";
import Admin from "./views/Admin";
import Predictor from "./views/Predictor";
import ProSection from "./views/Pro/ProSection";

const db = getFirestore(app);
const RTDB: Database = getDatabase(app);

function App() {
  const isPaused = false;
  const dispatch = useDispatch();
  const isMobileOrTablet = isMobile || isTablet;

  if (isMobileOrTablet && window.innerWidth <= window.innerHeight) {
    if (!localStorage.getItem("alertShown")) {
      // eslint-disable-next-line no-alert
      alert("Please use your phone in landscape mode for a better experience.");
      localStorage.setItem("alertShown", "true");
    }
  }

  const user = useSelector(
    (state: RootState) => state.userReducer.user,
    shallowEqual,
  );
  const game = useSelector(
    (state: RootState) => state.gameReducer.game,
    shallowEqual,
  );
  const lobby = useSelector(
    (state: RootState) => state.lobbyReducer.lobby,
    shallowEqual,
  );

  useEffect(() => {
    // Fetch latest LoL patch version from Riot API
    fetchLatestPatch();

    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      // Track daily visitor (1 write per session per user, ~free)
      if (firebaseUser) {
        const today = new Date().toISOString().split("T")[0];
        const visitKey = `visited_${today}`;
        if (!sessionStorage.getItem(visitKey)) {
          sessionStorage.setItem(visitKey, "1");
          setDoc(
            doc(db, "stats", "dailyVisitors"),
            { [today]: increment(1) },
            { merge: true },
          ).catch(() => {});
        }
      }
      if (firebaseUser) {
        const userDoc = doc(db, "users", firebaseUser.uid);
        const userSnapshot = await getDoc(userDoc);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          const newUser: User = {
            uid: firebaseUser.uid,
            currentElo: userData?.currentElo || defaultStartingElo,
            username: userData?.username || firebaseUser.uid,
            rankedGamesPlayed: userData?.rankedGamesPlayed || 0,
            subscriptionTier: userData?.subscriptionTier,
            subscriptionStatus: userData?.subscriptionStatus,
            dailyApiCalls: userData?.dailyApiCalls,
            dailyApiCallsDate: userData?.dailyApiCallsDate,
            predictorAccess: userData?.predictorAccess === true,
          };
          dispatch(setUser(newUser));
        }
      } else {
        dispatch(setUser(null));
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user?.uid) {
      const tmpDb = getFirestore();
      const userDocRef = doc(tmpDb, "users", user.uid);

      const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          dispatch(
            setUser({
              ...(docSnapshot.data() as User),
              uid: user.uid,
            }),
          );
        }
      });

      return () => unsubscribe();
    }
    return () => {};
  }, [user?.uid]);

  const navigate = useNavigate();
  // Invite users to update their username if it's too long
  useEffect(() => {
    if ((user?.username?.length || 0) > MAX_USERNAME_LENGTH) {
      // eslint-disable-next-line no-alert
      alert("Your username is too long, please update it.");
      navigate(`/user/${user?.uid}`);
    }
  }, [user?.username]);

  useEffect(() => {
    if (game?.id) {
      const tmpDb = getFirestore();
      const gameDocRef = doc(tmpDb, "games", game.id);

      const unsubscribe = onSnapshot(gameDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          if (
            docSnapshot.data().state === GameStateEnum.CANCELLED ||
            docSnapshot.data().state === GameStateEnum.ABORTED
          ) {
            dispatch(setGame(null));
          } else {
            const opponentId = docSnapshot
              .data()
              .players.filter((playerId: string) => playerId !== user?.uid)[0];

            // If we are in role_selection phase and the opponent have swap his champions and locked-in we dont want to update state of our player to keep the secret before the results phase
            if (
              !docSnapshot.data().rolesLockedIn.includes(opponentId) ||
              docSnapshot.data().rolesLockedIn.includes(user?.uid) ||
              docSnapshot.data().state !== GameStateEnum.ROLE_SELECTION
            ) {
              dispatch(
                setGame({ ...(docSnapshot.data() as Game), id: game.id }),
              );
            }
          }
        }
      });

      return () => unsubscribe();
    }

    return () => {};
  }, [game?.id]);

  useEffect(() => {
    if (lobby?.id) {
      const tmpDb = getFirestore();
      const lobbyDocRef = doc(tmpDb, "lobbies", lobby.id);

      const unsubscribe = onSnapshot(lobbyDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          if (
            docSnapshot.data().state === GameStateEnum.CANCELLED ||
            docSnapshot.data().state === GameStateEnum.ABORTED
          ) {
            dispatch(setGame(null));
          } else {
            dispatch(
              setLobby({ ...(docSnapshot.data() as Lobby), id: lobby.id }),
            );
          }
        }
      });

      return () => unsubscribe();
    }

    return () => {};
  }, [lobby?.id]);

  useEffect(() => {
    const auth = getAuth();
    auth.onAuthStateChanged(setUser);
  }, []);

  const location = useLocation();

  return (
    <div className="App" style={{ pointerEvents: isPaused ? "none" : "auto" }}>
      {!location.pathname.startsWith("/pro") && <Navbar />}
      {isPaused ? <PausePopup /> : null}

      <AnimatePresence mode="sync">
        <Routes location={location} key={location.key}>
          <Route
            path="/leaderboard"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <div className="pageContent">
                  <Leaderboard />
                </div>
                <FAQ />
                <Footer />
              </motion.div>
            }
          />

          <Route
            path="/lobby"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <div className="pageContent">
                  <CreateOrJoinGame RTDB={RTDB} />
                  <DebugFooter />
                </div>
                <FAQ />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/about"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <About />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/lab"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <div className="pageContent">
                  <Lab />
                </div>
                <FAQ />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/game"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 1,
                    delay: 0.2,
                    when: "beforeChildren",
                  },
                }}
              >
                <div className="pageContent" style={{ height: "100%" }}>
                  <GameView db={db} />
                  <DebugFooter />
                </div>
                <FAQ />
              </motion.div>
            }
          />
          <Route
            path="/user/:userUid"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <UserProfile />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <div className="pageContent">
                  <Home />
                </div>
                <FAQ />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/admin"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <Admin />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/privacy"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <PrivacyPolicy />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/terms"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <TermsOfService />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/contact"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <Contact />
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/predictor"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: 0.1,
                    when: "beforeChildren",
                  },
                  exit: {
                    opacity: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <div className="pageContent">
                  <Predictor />
                </div>
                <Footer />
              </motion.div>
            }
          />
          <Route
            path="/pro/*"
            element={
              <motion.div
                className="appContainer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 0.3 } }}
              >
                <ProSection />
              </motion.div>
            }
          />
          <Route path="/debug/:lobbyId" element={<DebugPage />} />
        </Routes>
      </AnimatePresence>
      {location.pathname !== "/game" &&
        !location.pathname.startsWith("/user") &&
        !location.pathname.startsWith("/pro") && (
          <div className="championPlaceholder" />
        )}
      {!location.pathname.startsWith("/pro") && <Friends />}
    </div>
  );
}

export default App;
